import Vue from 'vue'
import Vuex from 'vuex'
import createLogger from 'vuex/dist/logger'
import isEqual from 'lodash/isEqual'
import differenceWith from 'lodash/differenceWith'
import mapValues from 'lodash/mapValues'
import {
  DictionaryApi,
  CategoriesApi,
  SectionsApi,
  LanguagesApi,
  SourceDictionariesApi,
  WordsApi,
  LettersApi
} from '../common/api'
import { DEBUG } from '../common/config'

import {
  FETCH_CATEGORIES,
  FETCH_SECTIONS,
  CHANGE_DICTIONARY,
  FETCH_LANGUAGES,
  FETCH_SOURCE_DICTIONARIES,
  SOURCE_DICTIONARY_CREATE,
  LOAD_COMMON_DATA,
  MOVE_WORD,
  CHANGE_SOURCE_DICTIONARY,
  FETCH_SOURCE_WORDS,
  REFRESH_SOURCE_WORDS,
  TRANSLATION_DICTIONARY_CREATE,
  FETCH_TRANSLATIONS,
  FETCH_TRANSLATED_CATEGORIES,
  FETCH_TRANSLATED_SECTIONS,
  FETCH_ALPHABET,
  MOVE_LETTER,
  FETCH_TRANSLITERATED_ALPHABET,
  NEXT_TRANSLATION, SOURCE_DICTIONARY_UPDATE
} from './action-types'
import {
  SET_DICTIONARY,
  FETCH_WORDS_START,
  FETCH_WORDS_END,
  FETCH_CATEGORIES_END,
  FETCH_SECTIONS_END,
  ADD_SECTION,
  UPDATE_SECTION,
  ADD_CATEGORY,
  UPDATE_CATEGORY,
  ADD_WORD,
  UPDATE_WORD,
  SET_AUTH,
  PURGE_AUTH,
  REMOVE_WORD,
  FETCH_LANGUAGES_END,
  ADD_LANGUAGE,
  UPDATE_LANGUAGE,
  LOADING_START,
  LOADING_END,
  FETCH_SOURCE_DICTIONARIES_END,
  SOURCE_DICTIONARY_ADD,
  REORDER_WORDS,
  SET_SOURCE_DICTIONARY,
  TRANSLATION_DICTIONARY_ADD,
  FETCH_TRANSLATIONS_START,
  FETCH_TRANSLATIONS_END,
  ADD_TRANSLATION,
  UPDATE_TRANSLATION,
  FETCH_TRANSLATED_SECTIONS_END,
  FETCH_TRANSLATED_CATEGORIES_END,
  UPDATE_TRANSLATED_SECTION,
  UPDATE_TRANSLATED_CATEGORY,
  FETCH_ALPHABET_END,
  ADD_LETTER,
  UPDATE_LETTER,
  REORDER_LETTERS,
  FETCH_TRANSLITERATED_ALPHABET_END, ADD_TRANSLITERATED_LETTER, UPDATE_TRANSLITERATED_LETTER, SOURCE_DICTIONARY_CHANGE
} from './mutation-types'

Vue.use(Vuex)

const getSourceDictionaryId = (dictionaryId) => dictionaryId.split('-')[0]

const storeOptions = {
  state: {
    sourceDictionaries: [],
    sourceDictionary: {},
    dictionary: {},
    isAuthenticated: false,
    isLoading: false,
    errorOnLoad: null,
    words: [],
    wordParams: {},
    totalWords: null,
    pages: 0,
    categories: [],
    sections: [],
    languages: [],
    loaders: [],
    translations: [],
    translationParams: {},
    translatedSections: [],
    translatedCategories: [],
    letters: [],
    transliteratedLetters: []
  },
  getters: {
    isLoading (state) {
      return state.loaders.length > 0
    },
    dictionaryId (state) {
      return state.dictionary ? state.dictionary.id : null
    },
    sourceDictionaryId (state) {
      return state.sourceDictionary.id ? state.sourceDictionary.id : null
    },
    sourceLanguageId (state) {
      return state.sourceDictionary.language ? state.sourceDictionary.language.id : null
    },
    targetLanguageId (state) {
      return state.dictionary.targetLanguage ? state.dictionary.targetLanguage.id : null
    },
    sourceDictionaryName (state) {
      return state.sourceDictionary.name
    },
    dictionaryName (state) {
      return state.dictionary.name
    },
    getSourceDictionaryById: state => id => {
      return state.sourceDictionaries.find(dictionary => dictionary.id === id)
    },
    getDictionaryById: state => id => {
      const sourceId = getSourceDictionaryId(id)

      return state.sourceDictionaries.find(dictionary => dictionary.id === sourceId)
        .translationDictionaries.find(dictionary => dictionary.id === id)
    },
    languagesToCreateSourceDictionaries (state) {
      return differenceWith(state.languages, state.sourceDictionaries, (language, sourceDictionary) => {
        return language.id === sourceDictionary.language.id
      })
    }
  },
  mutations: {
    [LOADING_START] (state, action) {
      state.loaders.push(action)
    },
    [LOADING_END] (state, action) {
      let firstDeleted = false
      state.loaders = state.loaders.filter((val) => {
        if (firstDeleted) {
          return true
        }
        if (val === action) {
          firstDeleted = true
          return false
        }
        return true
      })
    },
    [FETCH_WORDS_START] (state, params) {
      state.wordParams = { ...params }
    },
    [FETCH_WORDS_END] (state, { items, pages, total }) {
      state.words = items
      state.pages = pages
      state.totalWords = total
    },
    [FETCH_TRANSLATIONS_START] (state, params) {
      state.translationParams = { ...params }
    },
    [FETCH_TRANSLATIONS_END] (state, { items, pages, total }) {
      state.translations = items
      state.pages = pages
      state.totalWords = total
    },
    [REORDER_WORDS] (state, words) {
      state.words = words
    },
    [REORDER_LETTERS] (state, letters) {
      state.letters = letters
    },
    [FETCH_CATEGORIES_END] (state, categories) {
      state.categories = categories
    },
    [FETCH_LANGUAGES_END] (state, languages) {
      state.languages = languages
    },
    [FETCH_SECTIONS_END] (state, sections) {
      state.sections = sections
    },
    [FETCH_TRANSLATED_SECTIONS_END] (state, translatedSections) {
      state.translatedSections = translatedSections
    },
    [FETCH_TRANSLATED_CATEGORIES_END] (state, translatedCategories) {
      state.translatedCategories = translatedCategories
    },
    [FETCH_SOURCE_DICTIONARIES_END] (state, sourceDictionaries) {
      state.sourceDictionaries = sourceDictionaries
    },
    [FETCH_ALPHABET_END] (state, alphabet) {
      state.letters = alphabet
    },
    [FETCH_TRANSLITERATED_ALPHABET_END] (state, alphabet) {
      state.transliteratedLetters = alphabet
    },
    [SET_DICTIONARY] (state, { dictionary }) {
      state.dictionary = dictionary
      state.words = []
      state.translatedCategories = []
      state.translatedSections = []
      state.transliteratedLetters = []
      state.totalWords = null
    },
    [ADD_SECTION] (state, section) {
      state.sections.push(section)
    },
    [UPDATE_SECTION] (state, section) {
      const key = state.sections.findIndex(obj => obj.id === section.id)
      state.sections.splice(key, 1, section)
    },
    [ADD_CATEGORY] (state, category) {
      state.categories.push(category)
    },
    [UPDATE_CATEGORY] (state, category) {
      const key = state.categories.findIndex(obj => obj.id === category.id)
      state.categories.splice(key, 1, category)
    },
    [ADD_LANGUAGE] (state, language) {
      state.languages.push(language)
    },
    [UPDATE_LANGUAGE] (state, language) {
      const key = state.languages.findIndex(obj => obj.id === language.id)
      state.languages.splice(key, 1, language)
    },
    [ADD_LETTER] (state, letter) {
      state.letters.push(letter)
    },
    [UPDATE_LETTER] (state, letter) {
      const key = state.letters.findIndex(obj => obj.id === letter.id)
      state.letters.splice(key, 1, letter)
    },
    [ADD_TRANSLITERATED_LETTER] (state, { letter, transliteratedLetter }) {
      state.transliteratedLetters.find(({ id }) => id === letter.id).transliteratedLetter = transliteratedLetter
    },
    [UPDATE_TRANSLITERATED_LETTER] (state, { letter, transliteratedLetter }) {
      state.transliteratedLetters.find(({ id }) => id === letter.id).transliteratedLetter = transliteratedLetter
    },
    [ADD_WORD] (state, word) {
      state.words.push(word)
      state.totalWords += 1
    },
    [UPDATE_WORD] (state, word) {
      const key = state.words.findIndex(obj => obj.id === word.id)
      state.words.splice(key, 1, word)
    },
    [REMOVE_WORD] (state, word) {
      state.words = state.words.filter(obj => obj.id !== word.id)
    },
    [ADD_TRANSLATION] (state, { word, translation }) {
      state.translations.find(({ id }) => id === word.id).translation = translation
    },
    [UPDATE_TRANSLATION] (state, { word, translation }) {
      state.translations.find(({ id }) => id === word.id).translation = translation
    },
    [UPDATE_TRANSLATED_SECTION] (state, { section, translation }) {
      state.translatedSections.find(({ id }) => id === section.id).translation = translation
    },
    [UPDATE_TRANSLATED_CATEGORY] (state, { category, translation }) {
      state.translatedCategories.find(({ id }) => id === category.id).translation = translation
    },
    [SET_AUTH] (state) {
      state.isAuthenticated = true
    },
    [PURGE_AUTH] (state) {
      state.isAuthenticated = false
    },
    [SOURCE_DICTIONARY_ADD] (state, sourceDictionary) {
      state.sourceDictionaries.push(sourceDictionary)
    },
    [TRANSLATION_DICTIONARY_ADD] (state, translationDictionary) {
      state.sourceDictionaries.find(dictionary => dictionary.id === translationDictionary.sourceLanguage.id)
        .translationDictionaries.push(translationDictionary)
    },
    [SET_SOURCE_DICTIONARY] (state, { sourceDictionary }) {
      state.sourceDictionary = sourceDictionary
      state.words = []
      state.letters = []
    },
    [SOURCE_DICTIONARY_CHANGE] (state, { sourceDictionary }) {
      const key = state.sourceDictionaries.findIndex(obj => obj.id === sourceDictionary.id)
      state.sourceDictionaries.splice(key, 1, sourceDictionary)
      if (state.sourceDictionary.id === sourceDictionary.id) {
        state.sourceDictionary = sourceDictionary
      }
    }
  },
  actions: {
    async [FETCH_TRANSLATIONS] ({ commit, state }, { ...params }) {
      commit(FETCH_TRANSLATIONS_START, params)
      const { data } = await DictionaryApi.query(state.dictionary.id, params)
      if (!isEqual(params, state.translationParams)) {
        console.log(`Skip fetched words, because current ${JSON.stringify(state.translationParams)}, but requested ${JSON.stringify(params)}`)
        return
      }
      commit(FETCH_TRANSLATIONS_END, data)
    },
    async [FETCH_SOURCE_WORDS] ({ commit, state }, { ...params }) {
      commit(FETCH_WORDS_START, params)
      const { data } = await WordsApi.query(params)
      if (!isEqual(params, state.wordParams)) {
        console.log(`Skip fetched words, because current ${JSON.stringify(state.wordParams)}, but requested ${JSON.stringify(params)}`)
        return
      }
      commit(FETCH_WORDS_END, data)
    },
    async [REFRESH_SOURCE_WORDS] ({ dispatch, state }) {
      dispatch(FETCH_SOURCE_WORDS, state.wordParams)
    },
    async [FETCH_CATEGORIES] ({ commit, state }) {
      const { data } = await CategoriesApi.query(state.sourceDictionary.id)
      commit(FETCH_CATEGORIES_END, data)
    },
    async [FETCH_SECTIONS] ({ commit, state }) {
      const { data } = await SectionsApi.query(state.sourceDictionary.id)
      commit(FETCH_SECTIONS_END, data)
    },
    async [FETCH_TRANSLATED_CATEGORIES] ({ commit, state }) {
      const { data } = await CategoriesApi.queryWithTranslations(state.dictionary.id)
      commit(FETCH_TRANSLATED_CATEGORIES_END, data)
    },
    async [FETCH_TRANSLATED_SECTIONS] ({ commit, state }) {
      const { data } = await SectionsApi.queryWithTranslations(state.dictionary.id)
      commit(FETCH_TRANSLATED_SECTIONS_END, data)
    },
    async [FETCH_LANGUAGES] ({ commit }) {
      const { data } = await LanguagesApi.query()
      commit(FETCH_LANGUAGES_END, data)
    },
    async [FETCH_ALPHABET] ({ commit, state }) {
      const { data } = await SourceDictionariesApi.alphabet(state.sourceDictionary)
      commit(FETCH_ALPHABET_END, data)
    },
    async [FETCH_TRANSLITERATED_ALPHABET] ({ commit, state }) {
      const { data } = await DictionaryApi.alphabet(state.dictionary)
      commit(FETCH_TRANSLITERATED_ALPHABET_END, data)
    },
    async [CHANGE_DICTIONARY] ({ commit, state, dispatch, getters }, { dictionaryId }) {
      if (getters.dictionaryId === dictionaryId) {
        return
      }
      commit(SET_DICTIONARY, { dictionary: getters.getDictionaryById(dictionaryId) })
      await Promise.all([
        dispatch(CHANGE_SOURCE_DICTIONARY, { sourceDictionaryId: getSourceDictionaryId(dictionaryId) }),
        dispatch(FETCH_TRANSLATED_SECTIONS),
        dispatch(FETCH_TRANSLATED_CATEGORIES)
      ])
    },
    async [CHANGE_SOURCE_DICTIONARY] ({ commit, state, dispatch, getters }, { sourceDictionaryId }) {
      if (getters.sourceDictionaryId === sourceDictionaryId) {
        return
      }
      commit(SET_SOURCE_DICTIONARY, { sourceDictionary: getters.getSourceDictionaryById(sourceDictionaryId) })
      await Promise.all([dispatch(FETCH_SECTIONS), dispatch(FETCH_CATEGORIES)])
    },
    async [FETCH_SOURCE_DICTIONARIES] ({ commit }) {
      const { data } = await SourceDictionariesApi.query()
      commit(FETCH_SOURCE_DICTIONARIES_END, data)
    },
    async [SOURCE_DICTIONARY_CREATE] ({ commit, state, dispatch }, sourceDictionary) {
      const { data } = await SourceDictionariesApi.create(sourceDictionary)
      commit(SOURCE_DICTIONARY_ADD, data)
    },
    async [TRANSLATION_DICTIONARY_CREATE] ({ commit, state, dispatch }, translationDictionary) {
      const { data } = await DictionaryApi.create(translationDictionary)
      commit(TRANSLATION_DICTIONARY_ADD, data)
    },
    async [LOAD_COMMON_DATA] ({ dispatch }) {
      await Promise.all([dispatch(FETCH_LANGUAGES), dispatch(FETCH_SOURCE_DICTIONARIES)])
    },
    async [MOVE_WORD] ({ dispatch, commit }, { word, position }) {
      const { data } = await WordsApi.move(word, position)
      commit(UPDATE_WORD, data)
      dispatch(REFRESH_SOURCE_WORDS)
    },
    async [MOVE_LETTER] ({ dispatch, commit }, { letter, position }) {
      const { data } = await LettersApi.move(letter, position)
      commit(UPDATE_LETTER, data)
      await dispatch(FETCH_ALPHABET)
    },
    async [NEXT_TRANSLATION] ({ dispatch, state }, { translation }) {
      const index = state.translations.indexOf(translation)
      if (index === -1) {
        throw new Error('Can not find transltion: ' + JSON.stringify(translation))
      }
      let nextTranslation = state.translations[index + 1]
      if (nextTranslation) {
        return nextTranslation
      }
      if (state.pages > state.translationParams.page) {
        // FIXME: в интерфейсе номер страницы останется прежним
        await dispatch(FETCH_TRANSLATIONS, { ...state.translationParams, page: state.translationParams.page + 1 })

        return state.translations[0]
      }
    },
    async [SOURCE_DICTIONARY_UPDATE] ({ commit }, { sourceDictionary }) {
      const { data } = await SourceDictionariesApi.update(sourceDictionary)
      commit(SOURCE_DICTIONARY_CHANGE, { sourceDictionary: data })
    }
  },
  strict: DEBUG,
  plugins: DEBUG ? [createLogger()] : []
}

storeOptions.actions = mapValues(storeOptions.actions, (func, name) => {
  return async (store, ...args) => {
    store.commit(LOADING_START, name)
    try {
      return await func(store, ...args)
    } finally {
      store.commit(LOADING_END, name)
    }
  }
})

export default new Vuex.Store(storeOptions)
